import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '@shared/api';
import { ModelLogo, providerName } from '@shared/brand';
import { dateTime, idr, num, relative } from '@shared/format';
import { CopyIcon } from '../components/CopyIcon';
import { Alert, Badge, Card, Empty, Icon, Loading, PageHeader, Segmented, Select, type SelectOption } from '../components/ui';
import '../styles/billing.css';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  currency: string;
  description: string | null;
  reference_id: string | null;
  created_at: string;
}

interface Integrity {
  wallet_total: number;
  ledger_total: number;
  drift: number;
  consistent: boolean;
}

type Tone = 'good' | 'warn' | 'critical' | 'accent' | 'neutral';
type Range = 'all' | 'today' | '7d' | '30d' | 'custom';

const PAGE = 50;
const BATCH = 200;
/** Ranged fetch ceiling. */
const RANGE_CAP = 2000;

const TYPES = ['TOPUP', 'USAGE', 'RESERVE', 'RELEASE', 'REFUND', 'ADJUSTMENT', 'BONUS'];

const TYPE_META: Record<string, { label: string; tone: Tone; note: string; short: string }> = {
  TOPUP: { label: 'Top up', tone: 'good', note: 'Dana masuk dari pembayaran yang sudah dikonfirmasi.', short: 'Dana masuk dari pembayaran' },
  USAGE: { label: 'Pemakaian', tone: 'neutral', note: 'Biaya sebenarnya dari satu request, dipotong dari saldo.', short: 'Biaya request' },
  RESERVE: { label: 'Ditahan', tone: 'neutral', note: 'Dana disisihkan saat request mulai. Total saldo tidak berubah.', short: 'Disisihkan saat request mulai' },
  RELEASE: { label: 'Dilepas', tone: 'neutral', note: 'Sisa dana ditahan kembali ke saldo tersedia. Total tidak berubah.', short: 'Sisa dana ditahan kembali' },
  REFUND: { label: 'Refund', tone: 'good', note: 'Biaya dikembalikan, misalnya karena request bermasalah.', short: 'Biaya dikembalikan' },
  BONUS: { label: 'Bonus', tone: 'accent', note: 'Bonus saldo dari isi saldo atau promo.', short: 'Bonus saldo' },
  ADJUSTMENT: { label: 'Penyesuaian', tone: 'warn', note: 'Koreksi oleh tim kami. Entri lama tidak pernah diubah atau dihapus.', short: 'Koreksi oleh tim kami' },
};

const TYPE_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: 'all', label: 'Semua jenis', icon: <Icon name="list" size={15} /> },
  ...TYPES.map((t) => ({
    value: t,
    label: TYPE_META[t].label,
    description: TYPE_META[t].short,
    icon: <span className={`bl-tone bl-tone-${TYPE_META[t].tone}`} />,
  })),
];

const RANGES: ReadonlyArray<{ id: Range; label: string }> = [
  { id: 'all', label: 'Semua' },
  { id: 'today', label: 'Hari ini' },
  { id: '7d', label: '7 hari' },
  { id: '30d', label: '30 hari' },
  { id: 'custom', label: 'Pilih tanggal' },
];

const MODEL_PREFIX = /^(claude|gpt|o\d|chatgpt|gemini|gemma|grok|deepseek|kimi|glm|seed|dreamina|doubao|mock)/i;

function isoDay(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** Range bounds, local time. */
function bounds(range: Range, from: string, to: string): { since: Date | null; until: Date | null } {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  switch (range) {
    case 'today':
      return { since: startOfToday, until: null };
    case '7d':
      return { since: new Date(startOfToday.getTime() - 6 * 86_400_000), until: null };
    case '30d':
      return { since: new Date(startOfToday.getTime() - 29 * 86_400_000), until: null };
    case 'custom': {
      const since = from ? new Date(`${from}T00:00:00`) : null;
      const until = to ? new Date(`${to}T23:59:59.999`) : null;
      return { since, until };
    }
    default:
      return { since: null, until: null };
  }
}

/** Request id from description. */
function requestIdOf(row: Transaction): string | null {
  return row.description?.match(/\breq_[A-Za-z0-9]+/)?.[0] ?? null;
}

function signed(value: number): string {
  if (value < 0) return `−${idr(-value)}`;
  return `+${idr(value)}`;
}

function describe(row: Transaction): ReactNode {
  const text = row.description ?? '';
  if (!text) return <span className="muted">—</span>;
  if (/^Reserved for request/.test(text)) return 'Dana ditahan untuk request';
  if (/^Released unused reservation/.test(text)) return 'Sisa dana ditahan dikembalikan';
  const failed = text.match(/^Released reservation for request \S+: (.+)$/);
  if (failed) return `Request gagal, dana dikembalikan penuh (${failed[1]})`;
  if (/^Usage for request/.test(text)) return 'Biaya request';
  if (text === 'Settled by finalizer') return 'Biaya request (ditagih ulang otomatis)';
  const topup = text.match(/^Top-up via (\S+)/);
  if (topup) return `Top up via ${topup[1].replace(/_/g, ' ')}`;

  const [slug, ...rest] = text.split(' ');
  if (row.type === 'USAGE' && MODEL_PREFIX.test(slug)) {
    const detail = rest.join(' ');
    const via = detail.match(/^via (\S+)\s*(\(stream\))?$/);
    const video = detail.match(/^video (\d+)s$/);
    const image = detail.match(/^image x(\d+)$/);
    const extra = via
      ? `via ${providerName(via[1])}${via[2] ? ', streaming' : ''}`
      : video
        ? `video ${video[1]} detik`
        : image
          ? `${image[1]} gambar`
          : detail;
    return (
      <span className="bl-desc">
        <ModelLogo model={slug} size={22} />
        <span className="bl-desc-text">
          <span className="mono">{slug}</span>
          {extra && <span className="muted"> · {extra}</span>}
        </span>
      </span>
    );
  }
  return <span className="bl-desc-text" title={text}>{text}</span>;
}

export function Transactions() {
  const [rows, setRows] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState('all');
  const [range, setRange] = useState<Range>('all');
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 6 * 86_400_000)));
  const [to, setTo] = useState(() => isoDay(new Date()));
  const [offset, setOffset] = useState(0);
  const [capped, setCapped] = useState(false);
  const [integrity, setIntegrity] = useState<Integrity | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ranged = range !== 'all';
  // page client-side when ranged
  const fetchOffset = ranged ? 0 : offset;
  const span = bounds(range, from, to);
  const sinceIso = span.since?.toISOString() ?? '';
  const untilIso = span.until?.toISOString() ?? '';

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const typeQuery = type === 'all' ? '' : `&type=${type}`;
    (async () => {
      try {
        if (!ranged) {
          const result = await api<{ data: Transaction[]; total: number }>(
            `/v1/wallet/transactions?limit=${PAGE}&offset=${fetchOffset}${typeQuery}`,
          );
          if (!alive) return;
          setRows(result.data);
          setTotal(result.total);
          setCapped(false);
          return;
        }
        // stop past range
        const since = sinceIso ? new Date(sinceIso) : null;
        const until = untilIso ? new Date(untilIso) : null;
        const collected: Transaction[] = [];
        let done = false;
        let hitCap = false;
        for (let skip = 0; skip < RANGE_CAP && !done; skip += BATCH) {
          const result = await api<{ data: Transaction[]; total: number }>(
            `/v1/wallet/transactions?limit=${BATCH}&offset=${skip}${typeQuery}`,
          );
          for (const row of result.data) {
            const at = new Date(row.created_at);
            if (until && at > until) continue;
            if (since && at < since) {
              done = true;
              break;
            }
            collected.push(row);
          }
          if (result.data.length < BATCH) done = true;
          if (!done && skip + BATCH >= RANGE_CAP) hitCap = true;
        }
        if (!alive) return;
        setRows(collected);
        setTotal(collected.length);
        setCapped(hitCap);
      } catch (err) {
        if (alive) setError((err as Error).message);
      } finally {
        if (alive) {
          setLoading(false);
          setReady(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [type, fetchOffset, ranged, sinceIso, untilIso]);

  useEffect(() => {
    // billing role only
    api<Integrity>('/v1/wallet/integrity')
      .then(setIntegrity)
      .catch(() => setIntegrity(null));
  }, []);

  const visible = useMemo(() => (ranged ? rows.slice(offset, offset + PAGE) : rows), [rows, offset, ranged]);

  // range or page
  const scope = ranged ? rows : visible;
  const summary = useMemo(() => {
    let incoming = 0;
    let outgoing = 0;
    for (const row of scope) {
      if (row.amount > 0) incoming += row.amount;
      else outgoing += row.amount;
    }
    return { incoming, outgoing, net: incoming + outgoing, count: scope.length };
  }, [scope]);

  // usage rows lack id
  const requestIds = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      const id = requestIdOf(row);
      if (id && row.reference_id) map.set(row.reference_id, id);
    }
    return map;
  }, [rows]);

  const resetPage = () => setOffset(0);

  if (!ready) return <Loading label="Memuat transaksi" />;

  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + PAGE, total);

  const pager = (
    <div className="bl-pager">
      {loading && <span className="spinner" aria-label="Memuat" />}
      <span style={{ marginRight: 4 }}>
        {num(first)}–{num(last)} dari {num(total)}
      </span>
      <button
        className="btn btn-sm icon-btn"
        disabled={offset === 0 || loading}
        onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
        aria-label="Halaman sebelumnya"
      >
        <Icon name="left" size={15} />
      </button>
      <button
        className="btn btn-sm icon-btn"
        disabled={offset + PAGE >= total || loading}
        onClick={() => setOffset((o) => o + PAGE)}
        aria-label="Halaman berikutnya"
      >
        <Icon name="right" size={15} />
      </button>
    </div>
  );

  return (
    <div className="bl-ledger-page">
      <PageHeader
        title="Transaksi"
        subtitle="Setiap pergerakan saldo tercatat di ledger. Entri tidak pernah diubah atau dihapus."
        actions={
          <>
            <Select
              className="bl-type-select"
              value={type}
              options={TYPE_OPTIONS}
              aria-label="Jenis transaksi"
              onChange={(next) => {
                setType(next);
                resetPage();
              }}
            />
            <span className="bl-range">
              <Segmented
                label="Rentang waktu"
                options={RANGES}
                value={range}
                onChange={(next) => {
                  setRange(next);
                  resetPage();
                }}
              />
            </span>
          </>
        }
      />

      {range === 'custom' && (
        <div className="bl-daterange">
          <label className="bl-date">
            <span>Dari</span>
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => {
                setFrom(e.target.value);
                resetPage();
              }}
            />
          </label>
          <label className="bl-date">
            <span>Sampai</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => {
                setTo(e.target.value);
                resetPage();
              }}
            />
          </label>
        </div>
      )}

      {error && <Alert kind="error">{error}</Alert>}
      {integrity && !integrity.consistent && (
        <Alert kind="error">
          Ledger dan saldo berselisih {idr(integrity.drift)}. Hubungi tim support kami dan sertakan halaman ini.
        </Alert>
      )}
      {capped && (
        <Alert kind="info">Rentang ini panjang; yang ditampilkan {num(RANGE_CAP)} entri terbaru. Persempit tanggal untuk melihat sisanya.</Alert>
      )}

      <Card
        title="Entri ledger"
        description={
          <span className="bl-summary">
            Masuk <strong className={summary.incoming > 0 ? 'in' : undefined}>{signed(summary.incoming)}</strong>
            {' · '}Keluar <strong>{summary.outgoing < 0 ? signed(summary.outgoing) : idr(0)}</strong>
            {' · '}Bersih <strong>{signed(summary.net)}</strong>
            {ranged ? ' di rentang ini' : ' di halaman ini'}
          </span>
        }
        action={pager}
      >
        {visible.length === 0 ? (
          <Empty icon="list">{type === 'all' && range === 'all' ? 'Belum ada transaksi.' : 'Tidak ada transaksi untuk filter ini.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="bl-ledger">
              <thead>
                <tr>
                  <th>Waktu</th>
                  <th>Jenis</th>
                  <th>Keterangan</th>
                  <th>Request</th>
                  <th className="num">Jumlah</th>
                  <th className="num">Saldo setelah</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const meta = TYPE_META[row.type];
                  const reqId = requestIdOf(row) ?? (row.reference_id ? requestIds.get(row.reference_id) : undefined);
                  return (
                    <tr key={row.id} className={row.amount === 0 ? 'bl-dim' : undefined}>
                      <td className="nowrap bl-l-time" title={relative(row.created_at)}>
                        {dateTime(row.created_at)}
                      </td>
                      <td className="bl-l-type">
                        <span title={meta?.note}>
                          <Badge tone={meta?.tone ?? 'neutral'}>{meta?.label ?? row.type}</Badge>
                        </span>
                      </td>
                      <td className="bl-l-desc">{describe(row)}</td>
                      <td className={reqId || row.reference_id ? 'bl-l-req' : 'bl-l-req none'}>
                        {reqId ? (
                          <span className="bl-id dim" title={reqId}>
                            <span className="truncate">{reqId}</span>
                            <CopyIcon value={reqId} label="Salin request ID" />
                          </span>
                        ) : row.reference_id ? (
                          <span className="bl-id dim" title={row.reference_id}>
                            <span className="muted truncate">ref {row.reference_id.slice(0, 8)}</span>
                            <CopyIcon value={row.reference_id} label="Salin referensi" />
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="num bl-l-amt">
                        {/* hold moves, total same */}
                        {row.amount === 0 ? (
                          <span className="bl-amount zero">—</span>
                        ) : row.amount > 0 ? (
                          <span className="bl-amount in">+{idr(row.amount, true)}</span>
                        ) : (
                          <span className="bl-amount out">−{idr(-row.amount, true)}</span>
                        )}
                      </td>
                      <td className="num secondary bl-l-bal">{idr(row.balance_after, true)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="bl-foot">
        {integrity?.consistent ? (
          <span className="bl-note">
            <Icon name="success" size={15} />
            Ledger cocok: jumlah semua entri sama dengan total saldo Anda, {idr(integrity.wallet_total)}.
          </span>
        ) : (
          <span />
        )}
        {total > PAGE && pager}
      </div>
    </div>
  );
}
